import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import QuickBookingModal from './QuickBookingModal';
import './Navbar.css';

// Map language codes to ISO 3166-1 alpha-2 country codes for flag images
const LANG_TO_COUNTRY = {
  en: 'gb', zh: 'cn', ja: 'jp', ko: 'kr', ar: 'sa', he: 'il',
  uk: 'ua', cs: 'cz', hi: 'in', bn: 'bd', vi: 'vn',
};
function flagUrl(langCode) {
  const country = LANG_TO_COUNTRY[langCode] || langCode;
  return `https://flagcdn.com/48x36/${country}.png`;
}
function FlagImg({ code, alt }) {
  return (
    <img
      src={flagUrl(code)}
      alt={alt || code}
      width="20"
      height="20"
      style={{ borderRadius: '50%', objectFit: 'cover', display: 'inline-block', verticalAlign: 'middle' }}
      onError={e => { e.target.style.display = 'none'; }}
    />
  );
}

const NAV_LINKS = [
  { path: '/', label: { ro: 'Acasă', en: 'Home', de: 'Startseite', fr: 'Accueil', it: 'Home', es: 'Inicio' } },
  { path: '/camere', label: { ro: 'Camere', en: 'Rooms', de: 'Zimmer', fr: 'Chambres', it: 'Camere', es: 'Habitaciones' } },
  { path: '/meniu', label: { ro: 'Meniu', en: 'Menu', de: 'Speisekarte', fr: 'Menu', it: 'Menu', es: 'Menú' } },
  { path: '/servicii', label: { ro: 'Servicii', en: 'Services', de: 'Leistungen', fr: 'Services', it: 'Servizi', es: 'Servicios' } },
  { path: '/despre', label: { ro: 'Despre noi', en: 'About', de: 'Über uns', fr: 'À propos', it: 'Chi siamo', es: 'Nosotros' } },
  { path: '/contact', label: { ro: 'Contact', en: 'Contact', de: 'Kontakt', fr: 'Contact', it: 'Contatto', es: 'Contacto' } },
];

const CTA_LABELS = {
  ro: { booking: 'Solicită rezervare', whatsapp: 'WhatsApp' },
  en: { booking: 'Book now', whatsapp: 'WhatsApp' },
  de: { booking: 'Jetzt buchen', whatsapp: 'WhatsApp' },
  fr: { booking: 'Réserver', whatsapp: 'WhatsApp' },
  it: { booking: 'Prenota ora', whatsapp: 'WhatsApp' },
  es: { booking: 'Reservar', whatsapp: 'WhatsApp' },
};

function WhatsAppIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="15" height="15" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}

export default function Navbar() {
  const { activeLang, languages, switchLanguage, t } = useLanguage();
  const siteName = t('site_name', 'Eden Pensiune');
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingMode, setBookingMode] = useState('booking');
  const location = useLocation();

  const cta = CTA_LABELS[activeLang] || CTA_LABELS.ro;
  const enableAvailabilityCheck = t('enable_availability_check', 'true') !== 'false';
  const contactPhone = t('contact_phone', '');

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  const openBooking = useCallback(() => {
    setMenuOpen(false);
    setBookingMode('booking');
    setBookingOpen(true);
  }, []);

  const openWhatsApp = useCallback(() => {
    setMenuOpen(false);
    setBookingMode('whatsapp');
    setBookingOpen(true);
  }, []);

  const activeLangObj = languages.find(l => l.code === activeLang);

  const langRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isHomePage = location.pathname === '/';

  return (
    <>
      <header className={`navbar${scrolled ? ' navbar--scrolled' : ''}${!isHomePage ? ' navbar--dark-page' : ''}`}>
        <div className="navbar__inner container">
          <Link to="/" className="navbar__logo">
            <img
              src="/LogoCasaEden.png"
              alt={siteName}
              className="navbar__logo-img"
            />
          </Link>

          <nav className={`navbar__nav${menuOpen ? ' navbar__nav--open' : ''}`}>
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`navbar__link${location.pathname === link.path ? ' navbar__link--active' : ''}`}
              >
                {link.label[activeLang] || link.label.ro}
              </Link>
            ))}

            {/* CTA buttons — inside nav so they appear in mobile overlay too */}
            <div className="navbar__cta-group">
              {enableAvailabilityCheck ? (
                <button className="navbar__btn navbar__btn--booking" onClick={openBooking}>
                  {cta.booking}
                </button>
              ) : (
                contactPhone && (
                  <span className="navbar__phone-text">
                    📞 {contactPhone}
                  </span>
                )
              )}
              <button className="navbar__btn navbar__btn--whatsapp navbar__btn--wa-nav" onClick={openWhatsApp}>
                <WhatsAppIcon />
                {cta.whatsapp}
              </button>
            </div>
          </nav>

          <div className="navbar__actions">
            {/* Language switcher */}
            <div className="lang-switcher" ref={langRef}>
              <button
                className="lang-switcher__btn"
                onClick={() => setLangOpen((o) => !o)}
                aria-label="Switch language"
              >
                <FlagImg code={activeLang || 'ro'} alt={activeLangObj?.name} />
                <span className="lang-switcher__arrow">▾</span>
              </button>
              {langOpen && (
                <ul className="lang-switcher__dropdown">
                  {languages.filter(l => l.is_active).map((lang) => (
                    <li key={lang.code}>
                      <button
                        className={`lang-switcher__item${activeLang === lang.code ? ' lang-switcher__item--active' : ''}`}
                        onClick={() => { switchLanguage(lang.code); setLangOpen(false); }}
                      >
                        <FlagImg code={lang.code} alt={lang.name} />
                        <span>{lang.native_name || lang.name}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Hamburger */}
            <button
              className={`navbar__hamburger${menuOpen ? ' navbar__hamburger--open' : ''}`}
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Toggle menu"
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
      </header>

      {/* Floating WhatsApp button — mobile only */}
      <button className="whatsapp-float" onClick={openWhatsApp} aria-label="WhatsApp rezervare">
        <WhatsAppIcon />
      </button>

      <QuickBookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} mode={bookingMode} />
    </>
  );
}
